import React from 'react'
import { ProgressMeter, ProgressStepper, Step } from '@paypalcorp/pp-react'
import { useExperiments } from 'ppbl/hooks'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { trackPage } from 'ppbl/utils/fpti-helper'
import clientLogger from 'ppbl/utils/client-logger'

type Props = {
  path: string
  applicationType: string
  creditApplicationLoading: boolean
}

type ProgressMeterValues = {
  max: string
  value: string
  steps: string
  title: string
}

const withProgressMeter = function (
  progressMeterValues: ProgressMeterValues,
  Component: React.ComponentType<Props>,
) {
  return function WrappedComponent(props: Props) {
    const experiments = useExperiments()

    const {
      ppbl_application_progress_bar_experiment:
        progressMeterVersion = 'control',
    } = experiments

    const progressStepperLocalisations = [
      {
        title: getLocalizedText('application.index.page1FullTitle'),
      },
      {
        title: getLocalizedText('application.index.page2FullTitle'),
      },
      {
        title: getLocalizedText('application.index.page3Title'),
      },
      {
        title: getLocalizedText('application.index.page4Title'),
      },
      {
        title: getLocalizedText('application.index.page5Title'),
      },
    ]

    const renderProgressBar = () => {
      if (progressMeterVersion === 'treatment_1') {
        trackPage({
          page: `noProgressBarApplicationPage${progressMeterValues?.value}`,
        })

        clientLogger.info(
          `No Progress Meter is rendered for the application - experiment version: ${progressMeterVersion}`,
        )
        return <div className='noProgressMeterMargin'/>
      }

      if (progressMeterVersion === 'treatment_2') {
        trackPage({
          page: `progressStepperApplicationPage${progressMeterValues?.value}`,
        })

        clientLogger.info(
          `Application is loaded with Progress Stepper, - experiment version: ${progressMeterVersion}`,
        )
        return (
          <div className="applicationProgressMeter">
            <ProgressStepper data-testid="progress-stepper">
              {progressStepperLocalisations.map((progressInfo, index) => {
                const stepNumber = index + 1
                let status: 'success' | 'default'
                const isCurrentStep =
                  stepNumber.toString() === progressMeterValues.value
                if (stepNumber.toString() <= progressMeterValues.value) {
                  status = 'success'
                } else {
                  status = 'default'
                }
                return (
                  <Step
                    key={index}
                    title={progressInfo.title}
                    status={status}
                    isNumeric={
                      stepNumber.toString() >= progressMeterValues.value
                    }
                    isCurrent={isCurrentStep}
                  />
                )
              })}
            </ProgressStepper>
          </div>
        )
      }

      if (progressMeterVersion === 'treatment_3') {
        trackPage({
          page: `partialProgressBarApplicationPage${progressMeterValues?.value}`,
        })

        clientLogger.info(
          `Application is loaded with Progress Meter excluding first step, - experiment version: ${progressMeterVersion}`,
        )

        if (progressMeterValues.value === '1') {
          return <div className='noProgressMeterMargin'/>
        }

        return (
          <ProgressMeter
            max={progressMeterValues.max}
            value={progressMeterValues.value}
            steps={progressMeterValues.steps}
            title={progressMeterValues.title}
            className="applicationProgressMeter"
            data-testid="progress-meter"
          />
        )
      }

      trackPage({
        page: `progressBarApplicationPage${progressMeterValues?.value}`,
      })

      clientLogger.info(
        'No treatement present, application is loaded with older version of Progress Meter',
      )
      return (
        <ProgressMeter
          max={progressMeterValues.max}
          value={progressMeterValues.value}
          steps={progressMeterValues.steps}
          title={progressMeterValues.title}
          className="applicationProgressMeter"
          data-testid="progress-meter"
        />
      )
    }

    return (
      <>
        <div>{renderProgressBar()}</div>
        <Component {...props} />
      </>
    )
  }
}

export default withProgressMeter
